import React from "react";
import { format, parseISO } from "date-fns";
import {
    BANK_RECONCILIATION_REPORT_LABELS,
    balancesAsOfLabel,
    beginningReconciledBalanceLabel,
    bankReconciliationReportDirectionalLabels,
} from "@common/constants/reports/bank-reconciliation-report";
import { fullDateFullYearFormat } from "@common/helpers/date";
import { currencyFormatter } from "@common/helpers/currency";
import { ReportCollapsibleSection } from "@components/accounting/ReportCollapsibleSection";
import {
    BankReconciliationReport,
    BankReconciliationReportGroup,
} from "./types";
import styles from "./BankReconciliationReport.module.scss";

interface SummaryRowProps {
    label: React.ReactNode;
    value: string;
    isTotal?: boolean;
}

const SummaryRow: React.FC<SummaryRowProps> = ({ label, value, isTotal }) => (
    <div
        className={isTotal ? styles.summaryRowTotal : styles.summaryRow}
        data-testid="bank-reconciliation-report-summary-row"
    >
        <span
            className={isTotal ? styles.summaryLabelTotal : styles.summaryLabel}
        >
            {label}
        </span>
        <span className={styles.summaryValue}>{value}</span>
    </div>
);

export const BankReconciliationReportSummary: React.FC<{
    report: BankReconciliationReport;
    clearedGroup: BankReconciliationReportGroup;
    unclearedGroup: BankReconciliationReportGroup;
}> = ({ report, clearedGroup, unclearedGroup }) => {
    const closingDateLabel = format(
        parseISO(report.periodClosingDate),
        fullDateFullYearFormat,
    );
    const openingDateLabel = format(
        parseISO(report.periodOpeningDate),
        fullDateFullYearFormat,
    );
    const statementBalanceDisplay = report.hasStatementBalance
        ? currencyFormatter.format(report.statementEndingBalance)
        : BANK_RECONCILIATION_REPORT_LABELS.statementBalanceNotAvailable;
    const differenceDisplay =
        report.differenceToReconcile === null
            ? "-"
            : currencyFormatter.format(report.differenceToReconcile);
    const directionalLabels = bankReconciliationReportDirectionalLabels(
        report.isLiability ? "liability" : "bank",
    );

    return (
        <ReportCollapsibleSection
            title={balancesAsOfLabel(closingDateLabel)}
            defaultOpen
        >
            <div className={styles.summaryRows}>
                <SummaryRow
                    label={beginningReconciledBalanceLabel(openingDateLabel)}
                    value={currencyFormatter.format(report.openingBalance)}
                />
                <SummaryRow
                    label={
                        <span className={styles.summaryLabelWithCount}>
                            <span>{directionalLabels.cashIn}</span>
                            <span className={styles.inlineCount}>
                                {clearedGroup.deposits.length}
                            </span>
                        </span>
                    }
                    value={currencyFormatter.format(clearedGroup.depositsTotal)}
                />
                <SummaryRow
                    label={
                        <span className={styles.summaryLabelWithCount}>
                            <span>{directionalLabels.cashOut}</span>
                            <span className={styles.inlineCount}>
                                {clearedGroup.payments.length}
                            </span>
                        </span>
                    }
                    value={currencyFormatter.format(clearedGroup.paymentsTotal)}
                />
                <SummaryRow
                    label={
                        BANK_RECONCILIATION_REPORT_LABELS.cashBalancePerBankStatement
                    }
                    value={statementBalanceDisplay}
                />
                <SummaryRow
                    label={
                        BANK_RECONCILIATION_REPORT_LABELS.endingReconciledBalance
                    }
                    value={currencyFormatter.format(report.closingBalance)}
                />
                <SummaryRow
                    label={
                        <span className={styles.summaryLabelWithCount}>
                            <span>
                                {
                                    BANK_RECONCILIATION_REPORT_LABELS.unreconciledTransactionsSummary
                                }
                            </span>
                            <span className={styles.inlineCount}>
                                {unclearedGroup.deposits.length +
                                    unclearedGroup.payments.length}
                            </span>
                        </span>
                    }
                    value={currencyFormatter.format(report.totalUnreconciled)}
                />
                <SummaryRow
                    label={
                        BANK_RECONCILIATION_REPORT_LABELS.endingPeriodBalance
                    }
                    value={currencyFormatter.format(report.bookBalance)}
                />
                <SummaryRow
                    label={
                        BANK_RECONCILIATION_REPORT_LABELS.differenceToReconcile
                    }
                    value={differenceDisplay}
                    isTotal
                />
            </div>
        </ReportCollapsibleSection>
    );
};
